import React from 'react';
import Button from 'react-bootstrap/Button';
import RequestMatcher from 'components/transactionEditor/RequestMatcher';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import BzmIcon from '../icons/BzmIcon';
import { Matcher } from '../../types/rest/dslTypes';

export interface RequestMatcherItem {
  id: string;
  name: string | null;
  operator: string | null | Matcher;
  value: string | null;
  isParsedFromUrl: boolean | null;
  isRequired?: boolean;
  [key: string]: string | boolean | null;
}

interface RequestMatchersListProps {
  id: string;
  matcherList: RequestMatcherItem[] | null;
  matcherTitle: string;
  emptyListMessage: string;
  nonEmptyListMessage: string;
  onListChange: (list: RequestMatcherItem[]) => void;
  isReadOnly?: boolean;
  isWizardModal?: boolean;
  shorTitle?: boolean;

  /** If true, matchers can be marked as optional */
  showIsRequiredCheckbox?: boolean;
  aiAssistantTitle?: string;
  transactionId?: number;
}

export const getUniqueIndex = (list: { id: string }[] | null) => {
  const index = list ? list.length : 0;
  if (index === 0) {
    return index;
  }
  const lastMatcher = _.last(list);
  if (lastMatcher) {
    const idIx = lastMatcher.id.lastIndexOf('-');
    if (idIx !== -1) {
      const idInLastToken = parseInt(lastMatcher.id.substr(idIx + 1, lastMatcher.id.length));
      if (!_.isNumber(idInLastToken)) {
        throw Error('Wrong format of the id in the list. The last string token of the id has to be a number.');
      }
      return idInLastToken + 1;
    }
  }
};

const RequestMatchersList = (props: RequestMatchersListProps) => {
  const { t } = useTranslation('transactions');
  const handleAddNew = (operator: string) => {
    const emptyItem = {
      id: `${props.id}-${getUniqueIndex(props.matcherList)}`,
      operator: operator,
      isRequired: true,
    } as RequestMatcherItem;
    const list = props.matcherList ? props.matcherList.concat(emptyItem) : [emptyItem];
    props.onListChange(list);
  };

  const handleDelete = (id: string) => {
    if (props.matcherList) {
      const list = props.matcherList.filter((item: RequestMatcherItem) => item.id !== id);
      props.onListChange(list);
    }
  };

  const handleChange = (name: string, value: string | boolean, id: string) => {
    if (props.matcherList) {
      const list = [...props.matcherList];
      const index: number = list.findIndex(item => item.id === id);
      if (index === -1) {
        console.log(`Cannot update value! Cannot find index of ${id} in the list.`);
      } else {
        list[index][name] = value;
        props.onListChange(list);
      }
    }
  };

  if (props.matcherList && props.matcherList.length > 0) {
    const length = props.matcherList.length;
    return (
      <div id={'qa-' + props.id}>
        <p>{props.nonEmptyListMessage}</p>
        {props.matcherList.map((item: RequestMatcherItem, index: number) => (
          <RequestMatcher
            onChange={handleChange}
            onAdd={handleAddNew}
            onDelete={() => handleDelete(item.id)}
            displayAdd={index === length - 1}
            key={item.id}
            id={item.id}
            matcherValues={item}
            matcherTitle={props.matcherTitle}
            isReadOnly={props.isReadOnly}
            isWizardModal={props.isWizardModal}
            shortTitle={props.shorTitle}
            showIsRequiredCheckbox={props.showIsRequiredCheckbox}
            aiAssistantTitle={props.aiAssistantTitle}
            transactionId={props.transactionId}
          />
        ))}
      </div>
    );
  }

  return (
    <div id={props.id}>
      <p>{props.emptyListMessage}</p>
      <Button
        disabled={props.isReadOnly}
        id={'qa-btn-add-first' + props.id}
        onClick={() => handleAddNew('equals')}
        className="btn-circular"
        title={t('editor.addNewMatcher', { name: props.matcherTitle })}
      >
        <BzmIcon variant={'icon-bzm-add'} />
      </Button>
    </div>
  );
};

export default RequestMatchersList;
